import { buildPorts, recommendedHostSize, recommendedClusterStorageSize } from '@system-atlas/shared';
import type { DeviceKind, EnvironmentType, FirewallData, IfaceZone, NodeType, SceneNode, SwitchData } from '@system-atlas/shared';
import { ENV_COLORS } from './colors';

let fallbackCounter = 0;

export function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `id-${Date.now()}-${fallbackCounter++}`;
}

interface CreateOpts {
  type: NodeType;
  environmentType?: EnvironmentType;
  x?: number;
  y?: number;
  deviceKind?: DeviceKind;
}

/** Erzeugt einen neuen Knoten mit sinnvollen Standardwerten je Typ. */
export function createNode({ type, environmentType, x = 60, y = 60, deviceKind }: CreateOpts): SceneNode {
  const env: EnvironmentType = environmentType ?? (type === 'cloud' ? 'cloud' : type === 'zone' ? 'cloud' : 'neutral');
  const colors = ENV_COLORS[env];
  const base = {
    id: newId(),
    type,
    x,
    y,
    label: '',
    environmentType: env,
    fill: colors.fill,
    stroke: colors.stroke,
  };

  switch (type) {
    case 'zone':
      return { ...base, width: 340, height: 240 };
    case 'circle':
      return { ...base, width: 120, height: 120 };
    case 'cloud':
      return { ...base, width: 170, height: 110 };
    case 'text':
      return { ...base, width: 200, height: 52, fill: '#ffffff', stroke: '#cbd5e1', fontSize: 16 };
    case 'rect':
      return { ...base, width: 160, height: 90 };
    case 'vlan-legend':
      return { ...base, width: 300, height: 180, fill: '#ffffff', stroke: '#cbd5e1' };
    case 'switch-legend':
      return { ...base, width: 340, height: 220, fill: '#ffffff', stroke: '#cbd5e1', switchLegend: { showAllPorts: false } };
    case 'device':
      return {
        ...base,
        width: 140,
        height: 130,
        fill: '#ffffff',
        stroke: '#cbd5e1',
        device: { kind: deviceKind ?? 'pc' },
      };
    case 'switch': {
      const sw: Omit<SwitchData, 'ports'> = { portCount: 48, sfpCount: 4, layer: 3, role: 'core' };
      return {
        ...base,
        width: 520,
        height: 96,
        fill: '#ffffff',
        stroke: '#334155',
        switch: { ...sw, ports: buildPorts(sw) },
      };
    }
    case 'host': {
      const size = recommendedHostSize(0);
      return {
        ...base,
        width: size.width,
        height: size.height,
        fill: '#ffffff',
        stroke: '#334155',
        host: { os: '', hypervisor: '', vms: [] },
      };
    }
    case 'firewall': {
      const iface = (name: string, zone: IfaceZone) => ({ id: newId(), name, zone });
      const fw: FirewallData = {
        role: 'firewall',
        interfaces: [iface('WAN', 'wan'), iface('LAN', 'lan')],
        rules: [],
        nat: [],
        vpns: [],
      };
      return { ...base, width: 320, height: 120, fill: '#ffffff', stroke: '#334155', firewall: fw };
    }
    case 'firewall-legend':
      return {
        ...base,
        width: 360,
        height: 220,
        fill: '#ffffff',
        stroke: '#cbd5e1',
        firewallLegend: { sections: ['rules'], showDisabled: false },
      };
    case 'clusterstorage': {
      const size = recommendedClusterStorageSize(0);
      return {
        ...base,
        width: size.width,
        height: size.height,
        fill: '#ffffff',
        stroke: '#334155',
        clusterStorage: { cluster: '', volumes: [] },
      };
    }
    case 'image':
    default:
      return { ...base, width: 120, height: 120 };
  }
}
